import { forwardRef, useId, useRef, useState } from "react";
import { ChevronDownIcon, ExclamationCircleIcon, InformationCircleIcon, ArrowUpTrayIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { cx } from "../../utils/cx";

const CONTROL =
  "w-full rounded-lg border-2 border-slate-100 bg-white px-4 text-slate-900 outline-none transition duration-100 " +
  "placeholder:text-slate-400 focus:border-brand/30 focus:ring-2 focus:ring-brand/10 " +
  "disabled:cursor-not-allowed disabled:bg-slate-50 read-only:bg-slate-50";

const ERROR = "border-red-300 focus:border-red-400 focus:ring-red-100";

// Label, hint and error message around any form control
// `htmlFor` also names the hint and the error (`<id>-info`, `<id>-error`), which the control reads out
export const describedBy = (id, info, error) => [info && `${id}-info`, error && `${id}-error`].filter(Boolean).join(" ") || undefined;

export function Field({ label, required, info, error, htmlFor, className, children }) {
  return (
    <div className={cx("flex w-full flex-col gap-2", className)}>
      {label && (
        <label htmlFor={htmlFor} className="text-sm font-medium text-slate-600">
          {label}
          {required && <sup className="ml-0.5 font-black text-red-500">*</sup>}
        </label>
      )}
      {info && (
        <p id={htmlFor && `${htmlFor}-info`} className="flex items-center gap-2 text-xs text-slate-500">
          <InformationCircleIcon className="h-4 w-4 shrink-0" />
          {info}
        </p>
      )}
      {children}
      {error && (
        <p id={htmlFor && `${htmlFor}-error`} className="flex items-center gap-1 text-xs font-medium text-red-600">
          <ExclamationCircleIcon className="h-4 w-4 shrink-0" />
          {error}
        </p>
      )}
    </div>
  );
}

export const Input = forwardRef(function Input(
  { label, required, info, error, prefix, suffix, className, inputClassName, id, ...props },
  ref
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  return (
    <Field label={label} required={required} info={info} error={error} htmlFor={inputId} className={className}>
      <div className="relative flex items-center">
        {prefix && <span className="pointer-events-none absolute left-4 text-slate-400">{prefix}</span>}
        <input
          ref={ref}
          id={inputId}
          required={required}
          aria-invalid={Boolean(error)}
          aria-describedby={describedBy(inputId, info, error)}
          className={cx(CONTROL, "h-[46px]", prefix && "pl-11", suffix && "pr-11", error && ERROR, inputClassName)}
          {...props}
        />
        {suffix && <span className="absolute right-4 text-slate-400">{suffix}</span>}
      </div>
    </Field>
  );
});

// Native select styled like the inputs; `options` is [{ value, label }] or children
export const Select = forwardRef(function Select(
  { label, required, info, error, options, placeholder, className, inputClassName, id, children, ...props },
  ref
) {
  const autoId = useId();
  const selectId = id ?? autoId;
  return (
    <Field label={label} required={required} info={info} error={error} htmlFor={selectId} className={className}>
      {/* appearance-none hides the native arrow so every browser shows the same chevron */}
      <div className="relative">
        <select
          ref={ref}
          id={selectId}
          required={required}
          aria-invalid={Boolean(error)}
          className={cx(CONTROL, "h-[46px] cursor-pointer appearance-none pr-10 [&>option]:text-slate-900", placeholder !== undefined && !props.value && "text-slate-400", error && ERROR, inputClassName)}
          {...props}
        >
          {placeholder !== undefined && <option value="">{placeholder}</option>}
          {options?.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
          {children}
        </select>
        <ChevronDownIcon className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
      </div>
    </Field>
  );
});

// For matching: lower case, without accents ("Côte" matches "cote")
const plain = (text) => String(text ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/**
 * A select the user can type in to filter a long list (countries, sponsors). Same props as Select;
 * onChange receives `{ target: { name, value } }`, so the same `name`-based handlers work. Arrow
 * keys move, Enter picks, Escape closes; an optional field (not `required`) can be cleared.
 */
export function SearchableSelect({ label, required, info, error, options = [], placeholder = "Select…", value, onChange, name, disabled, className, id, emptyText = "No matches" }) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const listId = `${inputId}-list`;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const listRef = useRef(null);
  const selected = options.find((o) => String(o.value) === String(value ?? ""));
  const matches = query ? options.filter((o) => plain(o.label).includes(plain(query))) : options;

  const pick = (option) => {
    onChange?.({ target: { name, value: option ? option.value : "" } });
    setOpen(false);
    setQuery("");
  };
  const show = () => {
    if (disabled) return;
    setOpen(true);
    setActive(Math.max(0, matches.indexOf(selected)));
  };
  const move = (to) => {
    const next = Math.min(Math.max(to, 0), matches.length - 1);
    setActive(next);
    listRef.current?.children[next]?.scrollIntoView?.({ block: "nearest" });
  };
  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) show();
      else move(active + 1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      move(active - 1);
    } else if (e.key === "Enter" && open) {
      e.preventDefault();
      if (matches[active]) pick(matches[active]);
    } else if (e.key === "Escape" && open) {
      e.preventDefault();
      setOpen(false);
      setQuery("");
    }
  };

  return (
    <Field label={label} required={required} info={info} error={error} htmlFor={inputId} className={className}>
      <div className="relative">
        <input
          id={inputId}
          type="text"
          role="combobox"
          autoComplete="off"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && matches[active] ? `${listId}-${active}` : undefined}
          aria-invalid={Boolean(error)}
          aria-required={required}
          disabled={disabled}
          // Closed: the chosen label; open: what the user types, with the chosen label as the hint
          value={open ? query : selected?.label ?? ""}
          placeholder={open && selected ? selected.label : placeholder}
          onFocus={show}
          onClick={show}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
          onBlur={() => {
            setOpen(false);
            setQuery("");
          }}
          className={cx(CONTROL, "h-[46px] pr-16", error && ERROR)}
        />
        {!required && selected && !disabled && (
          <button
            type="button"
            aria-label={`Clear ${label ?? "the choice"}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => pick(null)}
            className="absolute right-9 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:text-slate-600"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
        <ChevronDownIcon className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
        {open && (
          <ul ref={listRef} id={listId} role="listbox" aria-label={label} className="absolute z-30 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 text-sm shadow-lg">
            {matches.length === 0 && <li className="px-4 py-2 text-slate-400">{emptyText}</li>}
            {matches.map((o, i) => (
              <li
                key={o.value}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={String(o.value) === String(value ?? "")}
                // Picked on mouse down, before the input loses focus and closes the list
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(o);
                }}
                onMouseEnter={() => setActive(i)}
                className={cx(
                  "cursor-pointer px-4 py-2 text-slate-800",
                  i === active && "bg-brand/10",
                  String(o.value) === String(value ?? "") && "font-semibold text-brand"
                )}
              >
                {o.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Field>
  );
}

// Native radio buttons for a short choice; `options` is [{ value, label }]. onChange receives the
// input's change event, so it works with the same `name`-based handlers as the other fields
export function RadioGroup({ label, name, value, options, onChange, required, info, error, disabled, className }) {
  const groupId = useId();
  return (
    <fieldset
      className={cx("flex w-full flex-col gap-2", className)}
      disabled={disabled}
      aria-invalid={Boolean(error) || undefined}
      aria-describedby={describedBy(groupId, info, error)}
    >
      {label && (
        <legend className="mb-2 text-sm font-medium text-slate-600">
          {label}
          {required && <sup className="ml-0.5 font-black text-red-500">*</sup>}
        </legend>
      )}
      <div className="flex flex-wrap gap-x-6 gap-y-2">
        {options.map((o) => (
          <label key={o.value} className="flex cursor-pointer items-center gap-2 text-sm text-slate-800">
            <input
              type="radio"
              name={name}
              value={o.value}
              checked={value === o.value}
              onChange={onChange}
              className="h-4 w-4 cursor-pointer accent-brand"
            />
            {o.label}
          </label>
        ))}
      </div>
      {info && (
        <p id={`${groupId}-info`} className="flex items-center gap-2 text-xs text-slate-500">
          <InformationCircleIcon className="h-4 w-4 shrink-0" />
          {info}
        </p>
      )}
      {error && (
        <p id={`${groupId}-error`} className="flex items-center gap-1 text-xs font-medium text-red-600">
          <ExclamationCircleIcon className="h-4 w-4 shrink-0" />
          {error}
        </p>
      )}
    </fieldset>
  );
}

export const Textarea = forwardRef(function Textarea(
  { label, required, info, error, className, inputClassName, id, rows = 4, ...props },
  ref
) {
  const autoId = useId();
  const textareaId = id ?? autoId;
  return (
    <Field label={label} required={required} info={info} error={error} htmlFor={textareaId} className={className}>
      <textarea
        ref={ref}
        id={textareaId}
        rows={rows}
        required={required}
        aria-invalid={Boolean(error)}
        aria-describedby={describedBy(textareaId, info, error)}
        className={cx(CONTROL, "py-3", error && ERROR, inputClassName)}
        {...props}
      />
    </Field>
  );
});

// Dashed drop zone; calls onChange with the chosen File (or null when cleared)
export function FileUpload({ label, required, info, error, accept, hint, file, onChange, className }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const pick = (files) => onChange?.(files?.[0] ?? null);
  const clear = () => {
    if (inputRef.current) inputRef.current.value = "";
    onChange?.(null);
  };

  return (
    <Field label={label} required={required} info={info} error={error} className={className}>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          pick(e.dataTransfer.files);
        }}
        className={cx(
          "relative rounded-lg border-2 border-dashed p-6 text-center transition-colors",
          dragging ? "border-brand/50 bg-brand/5" : "border-slate-200 bg-white",
          error && "border-red-300"
        )}
      >
        {file ? (
          <div className="flex items-center justify-between gap-3 text-left">
            <span className="truncate text-sm font-medium text-slate-700">{file.name}</span>
            <button type="button" onClick={clear} className="rounded-md p-1 text-slate-500 hover:bg-red-50 hover:text-red-600">
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <button type="button" onClick={() => inputRef.current?.click()} className="flex w-full flex-col items-center gap-2">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-100 bg-slate-50">
              <ArrowUpTrayIcon className="h-5 w-5 text-brand" />
            </span>
            <span className="text-sm text-slate-600">
              <span className="font-medium text-brand">Click to upload</span> or drag and drop
            </span>
            {hint && <span className="text-xs text-slate-400">{hint}</span>}
          </button>
        )}
        <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={(e) => pick(e.target.files)} />
      </div>
    </Field>
  );
}
